// src/admin/pages/roles/role.ts

export interface Feature {
  code: string;
  name: string;
  group: string;
}

export interface Role {
  id?: string;
  name: string;
  description: string;
  permissions: string[]; // array of feature codes
  createdAt?: Date;
  updatedAt?: Date;
  createdBy?: string;
}

export interface RoleFormData {
  name: string;
  description: string;
  permissions: string[]; // array of feature codes
}

// ─────────────────────────────────────────────────────────────────────────────
// HARDCODED FEATURES LIST
// Add / remove features here as your application grows.
// Each feature has a unique `code`, a display `name`, and a `group`
// so the UI can render them in logical sections.
// ─────────────────────────────────────────────────────────────────────────────
export const APP_FEATURES: Feature[] = [
  // Dashboard
  { code: 'dashboard.view', name: 'View Dashboard', group: 'Dashboard' },

  // Users
  { code: 'users.view', name: 'View Users', group: 'Users' },
  { code: 'users.edit', name: 'Edit User (Assign Role)', group: 'Users' },
  { code: 'users.toggle_status', name: 'Activate / Deactivate User', group: 'Users' },
  { code: 'users.delete', name: 'Delete User', group: 'Users' },
  { code: 'users.assign_role', name: 'Assign Role to User', group: 'Users' },

  // Roles
  { code: 'roles.view', name: 'View Roles', group: 'Roles' },
  { code: 'roles.create', name: 'Create Role', group: 'Roles' },
  { code: 'roles.edit', name: 'Edit Role', group: 'Roles' },
  { code: 'roles.delete', name: 'Delete Role', group: 'Roles' },

  // Products
  { code: 'products.view', name: 'View Products', group: 'Products' },
  { code: 'products.create', name: 'Create Product', group: 'Products' },
  { code: 'products.edit', name: 'Edit Product', group: 'Products' },
  { code: 'products.delete', name: 'Delete Product', group: 'Products' },

  // Category
  { code: 'category.view', name: 'View Categories', group: 'Category' },
  { code: 'category.create', name: 'Create Category', group: 'Category' },
  { code: 'category.edit', name: 'Edit Category', group: 'Category' },
  { code: 'category.delete', name: 'Delete Category', group: 'Category' },

  // Variant
  { code: 'variant.view', name: 'View Variants', group: 'Variant' },
  { code: 'variant.create', name: 'Create Variant', group: 'Variant' },
  { code: 'variant.edit', name: 'Edit Variant', group: 'Variant' },
  { code: 'variant.delete', name: 'Delete Variant', group: 'Variant' },

  // Brand
  { code: 'brand.view', name: 'View Brands', group: 'Brand' },
  { code: 'brand.create', name: 'Create Brand', group: 'Brand' },
  { code: 'brand.edit', name: 'Edit Brand', group: 'Brand' },
  { code: 'brand.delete', name: 'Delete Brand', group: 'Brand' },

  // Warehouse
  { code: 'warehouse.view', name: 'View Show Rooms', group: 'Show Room' },
  { code: 'warehouse.create', name: 'Create Show Room', group: 'Show Room' },
  { code: 'warehouse.edit', name: 'Edit Show Room', group: 'Show Room' },
  { code: 'warehouse.delete', name: 'Delete Show Room', group: 'Show Room' },
  { code: 'warehouse.transfer', name: 'Transfer Show Room Stock', group: 'Show Room' },

  // Import Orders
  { code: 'import_order.view', name: 'View Import Orders', group: 'Import Orders' },
  { code: 'import_order.create', name: 'Create Import Order', group: 'Import Orders' },
  { code: 'import_order.edit', name: 'Edit Import Order', group: 'Import Orders' },
  { code: 'import_order.delete', name: 'Delete Import Order', group: 'Import Orders' },
  { code: 'import_order.submit', name: 'Submit PO for Approval', group: 'Import Orders' }, // ADD
  { code: 'import_order.approve', name: 'Approve / Cancel PO', group: 'Import Orders' }, // ADD
  { code: 'import_order.order', name: 'Mark PO as Ordered', group: 'Import Orders' }, // ADD
  { code: 'import_order.receive', name: 'Mark PO as Received', group: 'Import Orders' }, // ADD
  { code: 'import_order.payment', name: 'Record PO Payment', group: 'Import Orders' },

  // Inventory Return
  { code: 'inventory_return.view', name: 'View PO Returns', group: 'Inventory Return' },
  { code: 'inventory_return.manage', name: 'Manage PO Returns', group: 'Inventory Return' },

  // Supplier
  { code: 'supplier.view', name: 'View Suppliers', group: 'Supplier' },
  { code: 'supplier.create', name: 'Create Supplier', group: 'Supplier' },
  { code: 'supplier.edit', name: 'Edit Supplier', group: 'Supplier' },
  { code: 'supplier.delete', name: 'Delete Supplier', group: 'Supplier' },

  // Reports
  { code: 'reports.view', name: 'View Reports', group: 'Reports' },
  { code: 'reports.create', name: 'Generate Report', group: 'Reports' }, // ADD
  { code: 'reports.delete', name: 'Delete Report', group: 'Reports' }, // ADD
  { code: 'reports.download', name: 'Download Report', group: 'Reports' }, // ADD

  // Costs
  { code: 'costs.view', name: 'View Costs', group: 'Costs' },
  { code: 'costs.create', name: 'Create Cost', group: 'Costs' },
  { code: 'costs.edit', name: 'Edit Cost', group: 'Costs' },
  { code: 'costs.delete', name: 'Delete Cost', group: 'Costs' },

  // Messages
  { code: 'messages.view', name: 'View Messages', group: 'Messages' },
  { code: 'messages.reply', name: 'Reply to Messages', group: 'Messages' },
  { code: 'messages.delete', name: 'Delete Messages', group: 'Messages' },

  // Reviews
  { code: 'reviews.view', name: 'View Reviews', group: 'Reviews' },
  { code: 'reviews.manage', name: 'Manage Reviews', group: 'Reviews' },

  // Blog
  { code: 'blog.view', name: 'View Blogs', group: 'Blog' },
  { code: 'blog.create', name: 'Create Blog', group: 'Blog' },
  { code: 'blog.edit', name: 'Edit Blog', group: 'Blog' },
  { code: 'blog.delete', name: 'Delete Blog', group: 'Blog' },
  { code: 'blog.publish', name: 'Publish / Unpublish Blog', group: 'Blog' },
  { code: 'blog.comments.manage', name: 'Manage Blog Comments', group: 'Blog' },

  // Newsletter
  { code: 'newsletter.view', name: 'View Newsletter', group: 'Newsletter' },
  { code: 'newsletter.manage', name: 'Manage Newsletter', group: 'Newsletter' },

  // Banner
  { code: 'banner.view', name: 'View Banner', group: 'Banner' },
  { code: 'banner.create', name: 'Upload Banner', group: 'Banner' },
  { code: 'banner.edit', name: 'Replace Banner', group: 'Banner' },
  { code: 'banner.delete', name: 'Delete Banner', group: 'Banner' },

  // Settings
  { code: 'settings.view', name: 'View Settings', group: 'Settings' },
  { code: 'settings.edit', name: 'Edit Settings', group: 'Settings' },

  // Reservations
  { code: 'reservations.view', name: 'View Reservations', group: 'Reservations' },
  { code: 'reservations.create', name: 'Create Reservation', group: 'Reservations' },
  { code: 'reservations.edit', name: 'Edit / Update Reservation', group: 'Reservations' },
  { code: 'reservations.delete', name: 'Delete Reservation', group: 'Reservations' },

  // Trade-Ins
  { code: 'tradein.view', name: 'View Trade-In Submissions', group: 'Trade-Ins' },
  { code: 'tradein.manage', name: 'Manage Trade-In Submissions', group: 'Trade-Ins' },

  // Sold Vehicles
  { code: 'sold.view', name: 'View Sold Vehicles', group: 'Sold Vehicles' },
  { code: 'sold.create', name: 'Record a Sale', group: 'Sold Vehicles' },
  { code: 'sold.edit', name: 'Edit Sale Details', group: 'Sold Vehicles' },

  // Customers (registered on the client website)
  { code: 'customers.view', name: 'View Customers', group: 'Customers' },
  { code: 'customers.edit', name: 'Activate / Deactivate Customer', group: 'Customers' },

  // Employees / HR
  { code: 'employees.view', name: 'View Employees', group: 'Employees' },
  { code: 'employees.create', name: 'Create Employee', group: 'Employees' },
  { code: 'employees.edit', name: 'Edit Employee', group: 'Employees' },
  { code: 'employees.delete', name: 'Delete Employee', group: 'Employees' },

  // Audit Log
  { code: 'audit.view', name: 'View Audit Log', group: 'Audit Log' },
];

// Derive unique groups in insertion order
export const FEATURE_GROUPS: string[] = Array.from(
  new Set(APP_FEATURES.map((f) => f.group))
);